import React, { useState, useEffect, useCallback, useRef } from "react";
import { Controller } from "react-hook-form";
import ControlledField from "./ControlledField";
import DynamicFieldsGrid from "./DynamicFieldsGrid";
import { formatCurrency } from "../../../../utils/format";

export default function SalesPriceSection({
  FloatingInput,
  FloatingNumber,
  CustomSelect,
  control,
  getValues,
  setValue,
  watch,
  cleanNumber,
  handleCurrencyBlur,
  onSalesPriceBlur,
  setUseMortgageBalance,
  ttbData,
  salesPriceFormattedTick,
  handleSellerCalculation,
}) {
  const [loansTotalSum, setLoansTotalSum] = useState(0);
  const updateInProgressRef = useRef(false);
  const prevSalesPriceRef = useRef("");

  // Helper to get values with fallback
  const getVal = useCallback(
    (path, fallback = "") => {
      try {
        if (typeof getValues === "function") {
          const v = getValues(path);          
          return v === undefined ? fallback : v;
        }
      } catch (e) {
        return fallback;
      }
      return fallback;
    },
    [getValues]
  );

  // Parse number from formatted string
  const parseNumber = useCallback((v) => {
    return Number(String(v || "").replace(/[^0-9.-]+/g, "")) || 0;
  }, []);

  // Loan type and owner carry states
  const loanType =
    (watch
      ? watch("loanInfoMeta.buyersLoanType")
      : getVal("loanInfoMeta.buyersLoanType")) || "Conventional";
  const owner1 = watch
    ? watch("loanInfoMeta.allowOwnerCarry1Check")
    : getVal("loanInfoMeta.allowOwnerCarry1Check", true);
  const owner2 = watch
    ? watch("loanInfoMeta.allowOwnerCarry2Check")
    : getVal("loanInfoMeta.allowOwnerCarry2Check", false);

  // Initialize owner carry defaults - only when loanType changes to Owner Carry
  useEffect(() => {
    if (loanType === "Owner Carry") {
      try {
        setValue("loanInfoMeta.allowOwnerCarry1Check", true, {
          shouldDirty: true,
          shouldTouch: true,
        });
        setValue("loanInfoMeta.allowOwnerCarry2Check", false, {
          shouldDirty: true,
          shouldTouch: true,
        });
      } catch (err) {
        // ignore
      }
    }
  }, [loanType, setValue]);

  // Update adjusted amount and notify parent
  const updateAdjustedAndNotify = useCallback(() => {
    if (updateInProgressRef.current) return;
    updateInProgressRef.current = true;

    try {
      const allow1 = getVal("loanInfoMeta.allowOwnerCarry1Check", false);
      const allow2 = getVal("loanInfoMeta.allowOwnerCarry2Check", false);
      const amt = getVal("loanInfoMeta.amount", "0.00") || "0.00";
      const amt2 = getVal("loanInfoMeta.amount2", "0.00") || "0.00";

      let adjusted = "0.00";
      if (allow2) adjusted = amt2;
      else if (allow1) adjusted = amt;

      const prev = getVal("loanInfoMeta.adjusted", "0.00") || "0.00";
      if (String(prev) !== String(adjusted)) {
        setValue("loanInfoMeta.adjusted", adjusted, {
          shouldDirty: true,
          shouldTouch: true,
        });
        try {
          typeof onSalesPriceBlur === "function" && onSalesPriceBlur();
        } catch (err) {
          // ignore
        }
      }
    } catch (err) {
      // ignore
    } finally {
      updateInProgressRef.current = false;
    }
  }, [getVal, setValue, onSalesPriceBlur]);

  // Recompute amounts when sales price changes
  useEffect(() => {
    const salesPriceRaw = getVal("salesPrice", "") || "";

    // Only proceed if sales price actually changed
    if (prevSalesPriceRef.current === salesPriceRaw) {
      return;
    }
    prevSalesPriceRef.current = salesPriceRaw;

    try {
      const spNum = parseNumber(salesPriceRaw);
      if (!spNum || spNum <= 0) return;

      // Amount 1
      const perc1Raw = getVal("loanInfoMeta.conventionalPercent", "") || "";
      const perc1 = parseNumber(perc1Raw);
      const allow1 = getVal("loanInfoMeta.allowOwnerCarry1Check", false);
      if (allow1 && perc1 >= 0) {
        const amt = (spNum * perc1) / 100;
        setValue("loanInfoMeta.amount", amt.toFixed(2), {
          shouldDirty: true,
          shouldTouch: true,
        });
      }

      // Amount 2
      const perc2Raw = getVal("loanInfoMeta.conventionalPercent2", "") || "";
      const perc2 = parseNumber(perc2Raw);
      const allow2 = getVal("loanInfoMeta.allowOwnerCarry2Check", false);
      if (allow2 && perc2 >= 0) {
        const amt2 = (spNum * perc2) / 100;
        setValue("loanInfoMeta.amount2", amt2.toFixed(2), {
          shouldDirty: true,
          shouldTouch: true,
        });
      }

      updateAdjustedAndNotify();
    } catch (err) {
      // ignore
    }
  }, [
    salesPriceFormattedTick,
    getVal,
    parseNumber,
    setValue,
    updateAdjustedAndNotify,
  ]);

  // Compute loans total
  const computeLoansTotalFromForm = useCallback(() => {
    try {
      const b1 = getValues
        ? getValues("loanInfoMeta.loansToBePaidBalance1")
        : 0;
      const b2 = getValues
        ? getValues("loanInfoMeta.loansToBePaidBalance2")
        : 0;
      const b3 = getValues
        ? getValues("loanInfoMeta.loansToBePaidBalance3")
        : 0;
      const sum = parseNumber(b1) + parseNumber(b2) + parseNumber(b3);
      setLoansTotalSum(sum);
      handleSellerCalculation && handleSellerCalculation();
      return sum;
    } catch (e) {
      return 0;
    }
  }, [getValues, parseNumber, handleSellerCalculation]);

  // Calculate loans total on mount and when loan balance fields change
  const balance1 = watch
    ? watch("loanInfoMeta.loansToBePaidBalance1")
    : getVal("loanInfoMeta.loansToBePaidBalance1");
  const balance2 = watch
    ? watch("loanInfoMeta.loansToBePaidBalance2")
    : getVal("loanInfoMeta.loansToBePaidBalance2");
  const balance3 = watch
    ? watch("loanInfoMeta.loansToBePaidBalance3")
    : getVal("loanInfoMeta.loansToBePaidBalance3");

  useEffect(() => {
    // Compute total when component mounts or when any balance changes
    computeLoansTotalFromForm();
  }, [balance1, balance2, balance3, computeLoansTotalFromForm]);

  // Handle auto estimate toggle
  const handleAutoEstimateToggle = useCallback(
    (checked) => {
    //  console.log("[Seller autoEstimateLoan] set by: SalesPrice toggle ->", checked);
      if (checked && ttbData) {
        setValue(
          "loanInfoMeta.loansToBePaidBalance1",
          ttbData.loanBalance?.balance ?? "0.00"
        );
        setValue(
          "loanInfoMeta.loansToBePaidRate1",
          ttbData.loanBalance?.interestRate ?? "0.00"
        );
      } else {
        const mortgageAmount = getValues("loanInfoMeta.mortgageAmount");
        setValue(
          "loanInfoMeta.loansToBePaidBalance1",
          mortgageAmount || "0.00"
        );
      }
      setUseMortgageBalance?.(checked);
    },
    [ttbData, setValue, getValues, setUseMortgageBalance]
  );

  // Handle owner carry 1 checkbox change
  const handleOwnerCarry1Change = useCallback(
    (checked) => {
      try {
        setValue("loanInfoMeta.allowOwnerCarry1Check", checked, {
          shouldDirty: true,
          shouldTouch: true,
        });

        const salesPriceRaw = getVal("salesPrice", "") || "";
        const spNum = parseNumber(salesPriceRaw);

        if (checked) {
          const percRaw = getVal("loanInfoMeta.conventionalPercent", "") || "";
          const perc = parseNumber(percRaw);
          if (spNum > 0 && perc > 0) {
            const amt = (spNum * perc) / 100;
            setValue("loanInfoMeta.amount", amt.toFixed(2), {
              shouldDirty: true,
              shouldTouch: true,
            });
          }
        } else {
          setValue("loanInfoMeta.allowOwnerCarry2Check", false, {
            shouldDirty: true,
            shouldTouch: true,
          });
          setValue("loanInfoMeta.conventionalPercent", "80.00", {
            shouldDirty: true,
            shouldTouch: true,
          });
          setValue("loanInfoMeta.amount", "0.00", {
            shouldDirty: true,
            shouldTouch: true,
          });
          setValue("loanInfoMeta.conventionalPercent2", "0.00", {
            shouldDirty: true,
            shouldTouch: true,
          });
          setValue("loanInfoMeta.amount2", "0.00", {
            shouldDirty: true,
            shouldTouch: true,
          });
        }

        updateAdjustedAndNotify();
      } catch (err) {
        // ignore
      }
    },
    [getVal, parseNumber, setValue, updateAdjustedAndNotify]
  );

  // Handle owner carry 2 checkbox change
  const handleOwnerCarry2Change = useCallback(
    (checked) => {
      try {
        setValue("loanInfoMeta.allowOwnerCarry2Check", checked, {
          shouldDirty: true,
          shouldTouch: true,
        });

        const salesPriceRaw = getVal("salesPrice", "") || "";
        const spNum = parseNumber(salesPriceRaw);

        if (checked) {
          const perc2Raw =
            getVal("loanInfoMeta.conventionalPercent2", "") || "";
          const perc2 = parseNumber(perc2Raw);
          if (spNum > 0 && perc2 > 0) {
            const amt2 = (spNum * perc2) / 100;
            setValue("loanInfoMeta.amount2", amt2.toFixed(2), {
              shouldDirty: true,
              shouldTouch: true,
            });
          }
        } else {
          setValue("loanInfoMeta.conventionalPercent2", "0.00", {
            shouldDirty: true,
            shouldTouch: true,
          });
          setValue("loanInfoMeta.amount2", "0.00", {
            shouldDirty: true,
            shouldTouch: true,
          });
        }

        updateAdjustedAndNotify();
      } catch (err) {
        // ignore
      }
    },
    [getVal, parseNumber, setValue, updateAdjustedAndNotify]
  );

  // Loans to be paid fields config
  const loansFields = [
    {
      key: "existing1st",
      label: "Existing 1st",
      className: "",
      balanceName: "loanInfoMeta.loansToBePaidBalance1",
      rateName: "loanInfoMeta.loansToBePaidRate1",
    },
    {
      key: "existing2nd",
      label: "Existing 2nd",
      className: "",
      balanceName: "loanInfoMeta.loansToBePaidBalance2",
      rateName: "loanInfoMeta.loansToBePaidRate2",
    },
    {
      key: "existing3rd",
      label: "Existing 3rd",
      className: "",
      balanceName: "loanInfoMeta.loansToBePaidBalance3",
      rateName: "loanInfoMeta.loansToBePaidRate3",
    },
  ];

  // Render loan item
  const renderLoanItem = (item) => {
    const balanceName = item.balanceName;
    const rateName = item.rateName;

    return (
      <>
        <div
          className="calc-frm-fld form-group sign-of-amount"
          key={balanceName}
        >
          <ControlledField
            name={balanceName}
            control={control}
            className={item.className}
            InputComponent={FloatingNumber}
            defaultValue={getVal(balanceName, "0.00")}
            placeholder=""
            label={item.label}
            type="text"
            cleanNumber={cleanNumber}
            handleCurrencyBlur={handleCurrencyBlur}
            onFormatted={() => {
              computeLoansTotalFromForm();
            }}
          />
        </div>

        <div
          className="calc-frm-fld form-group sign-of-percentage"
          key={rateName}
        >
          <ControlledField
            name={rateName}
            control={control}
            className={item.className}
            InputComponent={FloatingNumber}
            defaultValue={getVal(rateName, "")}
            placeholder=""
            label={item.label}
            type="text"
            cleanNumber={cleanNumber}
            handleCurrencyBlur={handleCurrencyBlur}
          />
        </div>
      </>
    );
  };

  return (
    <>
      <h3 className="m-top-24 m-btm-16">Sales Price (Required)</h3>
      <div className="calc-frm-field">
        <div className="calc-frm-fld form-group sign-of-amount">
          <ControlledField
            name="salesPrice"
            control={control}
            InputComponent={FloatingNumber}
            label="Sales Price"
            placeholder=""
            className="input-filled"
            type="text"
            defaultValue={getVal("salesPrice", "")}
            cleanNumber={cleanNumber}
            onFormatted={(name, formatted) => {
              onSalesPriceBlur?.(formatted);
            }}
          />
        </div>
      </div>

      {/* Loan Information */}
      <div className="calc-frm-field">
        <div className="calc-frm-fld form-group">
          <span className="heading-c">Loan Information</span>
        </div>
      </div>

      {/* Loan Type */}
      <div className="calc-frm-field">
        <div className="calc-frm-fld form-group">
          <label className="lbl-section">Loan Type</label>
          <Controller
            name="loanInfoMeta.buyersLoanType"
            control={control}
            defaultValue={getVal("loanInfoMeta.buyersLoanType", "Conventional")}
            render={({ field }) => (
              <CustomSelect
                {...field}
                placeholder={getVal("loanInfoMeta.buyersLoanType", "")}
                options={[
                  "FHA 203b",
                  "VA",
                  "USDA",
                  "Conventional",
                  "Cash",
                  "Owner Carry",
                ]}
                onChange={field.onChange}
              />
            )}
          />
        </div>
        {loanType === "Conventional" && (
          <div className="calc-frm-fld form-group sign-of-percentage">
            <ControlledField
              name="loanInfoMeta.conventionalPercent"
              control={control}
              defaultValue={getVal("loanInfoMeta.conventionalPercent", "0.00")}
              InputComponent={FloatingNumber}
              placeholder=""
              label="Conventional %"
              type="text"
              cleanNumber={cleanNumber}
              handleCurrencyBlur={handleCurrencyBlur}
            />
          </div>
        )}
      </div>

      {/* Buyer Down Payment */}
      <div className="calc-frm-field">
        <div className="calc-frm-fld form-group">
          <ControlledField
            name="loanInfoMeta.buyerDownPayment"
            control={control}
            InputComponent={FloatingNumber}
            className="input-filled"
            label="Buyer Down Payment $ / %"
            placeholder=""
            type="text"
            defaultValue={getVal("loanInfoMeta.buyerDownPayment", "")}
            cleanNumber={cleanNumber}
            handleCurrencyBlur={handleCurrencyBlur}
          />
        </div>
      </div>

      {/* Owner Carry Section */}
      {loanType === "Owner Carry" && (
        <>
          <div className="calc-frm-field field-baseline">
            {/* First Left division */}
            <div className="calc-frm-field-division">
              {/* Owner Carry 1 Checkbox */}
              <div className="calc-frm-fld form-group">
                <Controller
                  name="loanInfoMeta.allowOwnerCarry1Check"
                  control={control}
                  defaultValue={
                    !!getVal("loanInfoMeta.allowOwnerCarry1Check", false)
                  }
                  render={({ field }) => (
                    <p>
                      <input
                        type="checkbox"
                        checked={!!field.value}
                        onChange={(e) => {
                          field.onChange && field.onChange(e.target.checked);
                          handleOwnerCarry1Change(e.target.checked);
                        }}
                      />
                      <b>Owner Carry</b>
                    </p>
                  )}
                />
              </div>

              {/* 1st Loan */}
              {owner1 && (
                <>
                  <div className="calc-frm-fld form-group">
                    <span>
                      <b>1st Loan</b>
                    </span>
                  </div>

                  {/* Loan to Value % 1 */}
                  <div className="calc-frm-fld form-group">
                    <FloatingNumber
                      type="text"
                      name="loanInfoMeta.conventionalPercent"
                      value={getVal("loanInfoMeta.conventionalPercent", "")}
                      control={control}
                      placeholder="0.00"
                      className="inpt-pla"
                      onChange={(e) => {
                        setValue(
                          "loanInfoMeta.conventionalPercent",
                          e.target.value,
                          { shouldDirty: true, shouldTouch: true }
                        );
                      }}
                      onBlur={() => {
                        try {
                          handleCurrencyBlur(
                            "loanInfoMeta.conventionalPercent"
                          );
                          const salesPriceRaw = getVal("salesPrice", "") || "";
                          const spNum = parseNumber(salesPriceRaw);
                          const percRaw =
                            getVal("loanInfoMeta.conventionalPercent", "") ||
                            "";
                          const num = parseNumber(percRaw);
                          const allow1 = getVal(
                            "loanInfoMeta.allowOwnerCarry1Check",
                            false
                          );
                          if (allow1 && spNum > 0 && num >= 0) {
                            const amt = (spNum * num) / 100;
                            setValue("loanInfoMeta.amount", amt.toFixed(2), {
                              shouldDirty: true,
                              shouldTouch: true,
                            });
                            updateAdjustedAndNotify();
                          }
                        } catch (err) {
                          // ignore
                        }
                      }}
                    />
                    <label>Loan to Value %</label>
                  </div>


                  {/* Amount 1 */}
                  <div className="calc-frm-fld form-group sign-of-amount">
                    <FloatingNumber
                      type="text"
                      name="loanInfoMeta.amount"
                      control={control}
                      placeholder="0.00"
                      className="inpt-pla"
                      value={getVal("loanInfoMeta.amount", "")}
                      onChange={(e) => {
                        try {
                          const raw = e.target.value;
                          const amt = parseNumber(raw);
                          setValue("loanInfoMeta.amount", raw, {
                            shouldDirty: true,
                            shouldTouch: true,
                          });
                          const salesPriceRaw = getVal("salesPrice", "") || "";
                          const spNum = parseNumber(salesPriceRaw);
                          if (spNum > 0) {
                            const perc = (amt / spNum) * 100;
                            setValue(
                              "loanInfoMeta.conventionalPercent",
                              isFinite(perc) ? perc.toFixed(2) : "0.00",
                              {
                                shouldDirty: true,
                                shouldTouch: true,
                              }
                            );
                            updateAdjustedAndNotify();
                          }
                        } catch (err) {
                          // ignore
                        }
                      }}
                      onBlur={() => {
                        try {
                          handleCurrencyBlur("loanInfoMeta.amount");
                        } catch (err) {
                          // ignore
                        }
                      }}
                    />
                    <label>Loan Amount</label>
                  </div>

                  {/* Interest Rate 1 */}
                  <div className="calc-frm-fld form-group">
                    <FloatingNumber
                      type="text"
                      name="loanInfoMeta.interestRate1"
                      control={control}
                      placeholder="0.00"
                      className="inpt-pla"
                      value={getVal("loanInfoMeta.interestRate1", "")}
                      onChange={(e) => {
                        setValue("loanInfoMeta.interestRate1", e.target.value, {
                          shouldDirty: true,
                          shouldTouch: true,
                        });
                      }}
                      onBlur={() =>
                        handleCurrencyBlur("loanInfoMeta.interestRate1")
                      }
                    />
                    <label>Interest Rate %</label>
                  </div>

                  {/* Terms 1 */}
                  <div className="calc-frm-fld form-group">
                    <FloatingNumber
                      type="text"
                      name="loanInfoMeta.termsInYears1"
                      control={control}
                      placeholder="0.00"
                      className="inpt-pla"
                      value={getVal("loanInfoMeta.termsInYears1", "")}
                      onChange={(e) => {
                        setValue("loanInfoMeta.termsInYears1", e.target.value, {
                          shouldDirty: true,
                          shouldTouch: true,
                        });
                      }}
                      onBlur={() =>
                        handleCurrencyBlur("loanInfoMeta.termsInYears1")
                      }
                    />
                    <label>Amortized Term in Yr</label>
                  </div>

                  {/* Add Due Years 1 */}
                  <div className="calc-frm-fld form-group">
                    <FloatingNumber
                      type="text"
                      name="loanInfoMeta.addDueYear1"
                      control={control}
                      placeholder="0.00"
                      className="inpt-pla"
                      value={getVal("loanInfoMeta.addDueYear1", "")}
                      onChange={(e) => {
                        setValue("loanInfoMeta.addDueYear1", e.target.value, {
                          shouldDirty: true,
                          shouldTouch: true,
                        });
                      }}
                      onBlur={() =>
                        handleCurrencyBlur("loanInfoMeta.addDueYear1")
                      }
                    />
                    <label>Add Due Years</label>
                  </div>
                </>
              )}
            </div>

            {/* Second Right division */}
            <div className="calc-frm-field-division">
              {/* Owner Carry 2 Checkbox */}
              <div className="calc-frm-fld form-group">
                <Controller
                  name="loanInfoMeta.allowOwnerCarry2Check"
                  control={control}
                  defaultValue={
                    !!getVal("loanInfoMeta.allowOwnerCarry2Check", false)
                  }
                  render={({ field }) => (
                    <p>
                      <input
                        type="checkbox"
                        checked={!!field.value}
                        onChange={(e) => {
                          field.onChange && field.onChange(e.target.checked);
                          handleOwnerCarry2Change(e.target.checked);
                        }}
                      />
                      <b>Owner Carry</b>
                    </p>
                  )}
                />
              </div>

              {owner1 && (
                <div className="calc-frm-fld form-group">
                  <span>
                    <b>2nd Loan</b>
                  </span>
                </div>
              )}

              {/* Loan to Value % 2 */}
              {owner1 && (
                <div className="calc-frm-fld form-group">
                  <FloatingNumber
                    type="text"
                    name="loanInfoMeta.conventionalPercent2"
                    value={getVal("loanInfoMeta.conventionalPercent2", "")}
                    control={control}
                    placeholder="0.00"
                    className="inpt-pla"
                    onChange={(e) => {
                      setValue(
                        "loanInfoMeta.conventionalPercent2",
                        e.target.value,
                        { shouldDirty: true, shouldTouch: true }
                      );
                    }}
                    onBlur={() => {
                      try {
                        handleCurrencyBlur("loanInfoMeta.conventionalPercent2");
                        const salesPriceRaw = getVal("salesPrice", "") || "";
                        const spNum = parseNumber(salesPriceRaw);
                        const perc2Raw =
                          getVal("loanInfoMeta.conventionalPercent2", "") || "";
                        const num = parseNumber(perc2Raw);
                        const allow2 = getVal(
                          "loanInfoMeta.allowOwnerCarry2Check",
                          false
                        );
                        if (allow2 && spNum > 0 && num >= 0) {
                          const amt2 = (spNum * num) / 100;
                          setValue("loanInfoMeta.amount2", amt2.toFixed(2), {
                            shouldDirty: true,
                            shouldTouch: true,
                          });
                          updateAdjustedAndNotify();
                        }
                      } catch (err) {
                        // ignore
                      }
                    }}
                  />
                  <label>Loan to Value %</label>
                </div>
              )}

              {/* Amount 2 */}
              {owner1 && owner2 && (
                <div className="calc-frm-fld form-group sign-of-amount">
                  <FloatingNumber
                    type="text"
                    name="loanInfoMeta.amount2"
                    control={control}
                    placeholder="0.00"
                    className="inpt-pla"
                    value={getVal("loanInfoMeta.amount2", "")}
                    onChange={(e) => {
                      try {
                        const raw = e.target.value;
                        const amt2 = parseNumber(raw);
                        setValue("loanInfoMeta.amount2", raw, {
                          shouldDirty: true,
                          shouldTouch: true,
                        });
                        const salesPriceRaw = getVal("salesPrice", "") || "";
                        const spNum = parseNumber(salesPriceRaw);
                        if (spNum > 0) {
                          const perc2 = (amt2 / spNum) * 100;
                          setValue(
                            "loanInfoMeta.conventionalPercent2",
                            isFinite(perc2) ? perc2.toFixed(2) : "0.00",
                            {
                              shouldDirty: true,
                              shouldTouch: true,
                            }
                          );
                          updateAdjustedAndNotify();
                        }
                      } catch (err) {
                        // ignore
                      }
                    }}
                    onBlur={() => {
                      try {
                        handleCurrencyBlur("loanInfoMeta.amount2");
                      } catch (err) {
                        // ignore
                      }
                    }}
                  />
                  <label>Loan Amount</label>
                </div>
              )}

              {/* Interest Rate 2 */}
              {owner1 && owner2 && (
                <div className="calc-frm-fld form-group">
                  <FloatingNumber
                    type="text"
                    name="loanInfoMeta.interestRate2"
                    control={control}
                    placeholder="0.00"
                    className="inpt-pla"
                    value={getVal("loanInfoMeta.interestRate2", "")}
                    onChange={(e) => {
                      setValue("loanInfoMeta.interestRate2", e.target.value, {
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                    }}
                    onBlur={() =>
                      handleCurrencyBlur("loanInfoMeta.interestRate2")
                    }
                  />
                  <label>Interest Rate %</label>
                </div>
              )}

              {/* Terms 2 */}
              {owner1 && owner2 && (
                <div className="calc-frm-fld form-group">
                  <FloatingNumber
                    type="text"
                    name="loanInfoMeta.termsInYears2"
                    control={control}
                    placeholder="0.00"
                    className="inpt-pla"
                    value={getVal("loanInfoMeta.termsInYears2", "")}
                    onChange={(e) => {
                      setValue("loanInfoMeta.termsInYears2", e.target.value, {
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                    }}
                    onBlur={() =>
                      handleCurrencyBlur("loanInfoMeta.termsInYears2")
                    }
                  />
                  <label>Amortized Term in Yr</label>
                </div>
              )}

              {/* Add Due Years 2 */}
              {owner1 && owner2 && (
                <div className="calc-frm-fld form-group">
                  <FloatingNumber
                    type="text"
                    name="loanInfoMeta.addDueYear2"
                    control={control}
                    placeholder="0.00"
                    className="inpt-pla"
                    value={getVal("loanInfoMeta.addDueYear2", "")}
                    onChange={(e) => {
                      setValue("loanInfoMeta.addDueYear2", e.target.value, {
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                    }}
                    onBlur={() =>
                      handleCurrencyBlur("loanInfoMeta.addDueYear2")
                    }
                  />
                  <label>Add Due Years</label>
                </div>
              )}
            </div>
          </div>

        </>
      )}

      {/* Estimated Closing Date */}
      <div className="calc-frm-field">
        <div className="calc-frm-fld form-group">
          <Controller
            name="loanInfoMeta.estimatedClosingDate"
            control={control}
            defaultValue={getVal("loanInfoMeta.estimatedClosingDate", "")}
            render={({ field }) => (
              <FloatingInput
                {...field}
                type="date"
                label=""
                placeholder="MM/DD/YYYY"
                className="inpt-pla"
              />
            )}
          />
          <label htmlFor="input-amount1">Estimated Closing Date</label>
        </div>
      </div>

      {/* Loan to be Paid or Assumed */}
      <div className="calc-frm-field">
        <div className="calc-frm-fld form-group">
          <span className="heading-c">Loan to be Paid or Assumed</span>
          <div className="switch-wrap">
            <div className="switch-wrap-a">
              <Controller
                name="loanInfoMeta.autoEstimateLoan"
                control={control}
                defaultValue={!!getVal("loanInfoMeta.autoEstimateLoan", false)}
                render={({ field }) => (
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={!!field.value}
                      onChange={(e) => {
                        const checked = !!e.target.checked;
                        field.onChange(checked);
                        handleAutoEstimateToggle(checked);
                      }}
                    />
                    <span className="slider"></span>
                  </label>
                )}
              />
            </div>
            <div className="switch-wrap-b">
              <p className="tooltip">
                Auto Estimate Loan
                <div className="question-tooltip">
                  <ul>
                    <li>
                      <b>Balance</b> is derived from transaction history.
                    </li>
                    <li>
                      <b>Rate</b> is based on loan rates at the time of
                      origination.
                    </li>
                    <li>
                      This is only an estimate and may be different based on the
                      borrower's repayment history.
                    </li>
                  </ul>
                </div>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Loans to be Paid */}
      <div className="calc-frm-field">
        <div className="calc-frm-fld form-group">
          <span className="heading-c">Balance $</span>
        </div>
        <div className="calc-frm-fld form-group">
          <span className="heading-c">Rate %</span>
        </div>
      </div>

      <DynamicFieldsGrid
        fields={loansFields}
        control={control}
        FloatingInput={FloatingNumber}
        cleanNumber={cleanNumber}
        fullRowPerItem={true}
        renderItem={renderLoanItem}
      />

      <div className="calc-frm-field">
        <div className="calc-frm-fld form-group">&nbsp;</div>
        <div className="calc-frm-fld form-group heading-c">
          Total ${" "}
          {loansTotalSum && loansTotalSum > 0
            ? `${formatCurrency(loansTotalSum)}`
            : "0.00"}
        </div>
      </div>

      {/* Annual Property Tax */}
      <div className="calc-frm-field">
        <div className="calc-frm-fld form-group sign-of-amount">
          <ControlledField
            name="loanInfoMeta.annualPropertyTaxAmount"
            control={control}
            InputComponent={FloatingNumber}
            label="Annual Property Tax $"
            placeholder=""
            type="text"
            defaultValue={getVal("loanInfoMeta.annualPropertyTaxAmount", "")}
            cleanNumber={cleanNumber}
          />
        </div>
      </div>
    </>
  );
}
